import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { updateProfile, signOut } from 'firebase/auth';
import {
  FiUser,
  FiMail,
  FiHash,
  FiShield,
  FiCamera,
  FiLogOut,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';

import { AuthContext } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { db, auth } from '../utils/firebaseConfig';
import { presenceTracker } from '../utils/presenceTracker';
import ConfirmationModal from '../components/ConfirmationModal';
import AnimatedThemeToggle from '../components/AnimatedThemeToggle';
import defaultAvatar from '../assets/default-avatar.svg';

/* ------------------------------------------------------------------ */
// Utility to compress images before upload
const compressImage = (file) =>
  new Promise((res, rej) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = ({ target }) => {
      const img = new Image();
      img.src = target.result;
      img.onload = () => {
        const MAX = 500;
        let { width, height } = img;
        if (width > height && width > MAX) { height = (height * MAX) / width; width = MAX; }
        else if (height > MAX) { width = (width * MAX) / height; height = MAX; }

        const canvas = Object.assign(document.createElement('canvas'), { width, height });
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        res(canvas.toDataURL('image/webp', 0.8));
      };
      img.onerror = rej;
    };
    reader.onerror = rej;
  });

// Reusable info row component
const InfoRow = ({ icon: Icon, label, name, editable, value, onChange }) => (
  <div className="flex flex-col sm:flex-row sm:items-center py-2 border-b border-gray-300 dark:border-secondary-dark/20 last:border-0">
    <label className="font-medium flex items-center text-gray-700 dark:text-gray-200 mb-1 sm:mb-0 sm:w-1/3">
      <Icon className="mr-1.5 h-4 w-4 text-blue-600 dark:text-blue-400" />{label}
    </label>
    <div className="sm:w-2/3">
      {editable
        ? (
          <input
            className="w-full px-3 py-2 rounded-md bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none transition-colors"
            value={value || ''}
            onChange={(e) => onChange(prev => ({ ...prev, [name]: e.target.value }))}
          />
        )
        : <p className="text-gray-800 dark:text-gray-100 px-1">{value || 'N/A'}</p>}
    </div>
  </div>
);

export default function ProfilePage() {
  const { currentUser } = useContext(AuthContext);
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const inputFile = useRef();

  const [data, setData] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Fetch user profile on mount
  useEffect(() => {
    if (!currentUser) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', currentUser.uid));
        if (!snap.exists()) return toast.error('Profile not found');
        setData({
          ...snap.data(),
          photoURL: snap.data().photoURL || defaultAvatar,
        });
      } catch {
        toast.error('Could not load profile');
      }
    })();
  }, [currentUser]);


  // Handle new avatar upload
  const handlePhoto = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.type.startsWith('image/')) return toast.error('Please choose an image');
      if (file.size > 5 * 1024 * 1024) return toast.error('Max size 5 MB');

      setPhotoBusy(true);
      try {
        const img = await compressImage(file);
        const photoRef = `profile_${currentUser.uid}_${Date.now()}`;

        await updateDoc(doc(db, 'users', currentUser.uid), {
          photoURL: img,
          photoRef,
        });
        await updateProfile(currentUser, { photoURL: photoRef });
        setData((prev) => ({ ...prev, photoURL: img }));
        toast.success('Picture updated');
      } catch {
        toast.error('Upload failed');
      } finally {
        setPhotoBusy(false);
      }
    },
    [currentUser]
  );

  // Sign out handler
  const handleSignOut = useCallback(async () => {
    try {
      await presenceTracker.cleanup();
      await signOut(auth);
      toast.success('Signed out');
      navigate('/login');
    } catch {
      toast.error('Sign‑out failed');
    }
  }, [navigate]);

  return (
    <div className="min-h-screen p-4 bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark transition-colors duration-300">
      <div className="max-w-2xl mx-auto">
        <div 
          className="border rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl min-h-[600px]"
          style={{
            backgroundColor: 'var(--color-primary)',
            borderColor: 'var(--color-secondary)',
            color: 'var(--color-text)'
          }}
        >
          <div className="p-6 h-full">
            <AnimatePresence mode="wait">
              {!data ? (
                // Skeleton that matches the exact layout of the content
                <motion.div
                  key="skeleton"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="h-full flex flex-col"
                >
                  {/* Header skeleton */}
                  <div className="flex items-center justify-between mb-6 pb-4 border-b"
                       style={{ borderColor: 'var(--color-secondary)' }}>
                    <div className="flex items-center">
                      <div className="w-6 h-6 bg-secondary-light dark:bg-secondary-dark rounded mr-2 animate-pulse" />
                      <div className="w-16 h-6 bg-secondary-light dark:bg-secondary-dark rounded animate-pulse" />
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-20 h-9 bg-secondary-light dark:bg-secondary-dark rounded-lg animate-pulse" />
                      <div className="w-12 h-9 bg-secondary-light dark:bg-secondary-dark rounded-lg animate-pulse" />
                    </div>
                  </div>

                  {/* Avatar skeleton */}
                  <div className="flex justify-center mb-6 relative">
                    <div className="relative">
                      <div className="h-28 w-28 bg-secondary-light dark:bg-secondary-dark rounded-xl animate-pulse" />
                      <div className="absolute bottom-0 right-0 w-8 h-8 bg-secondary-light dark:bg-secondary-dark rounded-full animate-pulse"
                           style={{ transform: 'translate(20%, 20%)' }} />
                    </div>
                  </div>

                  {/* Info rows skeleton */}
                  <div className="space-y-4 p-4 rounded-lg border mb-6 flex-grow"
                       style={{
                         backgroundColor: 'var(--color-secondary)/20',
                         borderColor: 'var(--color-secondary)'
                       }}>
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="flex flex-col sm:flex-row sm:items-center py-2 border-b border-gray-300 dark:border-secondary-dark/20 last:border-0">
                        <div className="font-medium flex items-center mb-1 sm:mb-0 sm:w-1/3">
                          <div className="w-4 h-4 bg-secondary-light dark:bg-secondary-dark rounded mr-1.5 animate-pulse" />
                          <div className="w-20 h-4 bg-secondary-light dark:bg-secondary-dark rounded animate-pulse" />
                        </div>
                        <div className="sm:w-2/3">
                          <div className="w-full h-4 bg-secondary-light dark:bg-secondary-dark rounded animate-pulse" />
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ) : (
                // Actual content with smooth transition
                <motion.div 
                  key="content"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="h-full flex flex-col"
                >
                  {/* Header */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.1 }}
                    className="flex items-center justify-between mb-6 pb-4 border-b"
                    style={{ borderColor: 'var(--color-secondary)' }}
                  >
                    <h1 className="text-xl font-bold flex items-center" style={{ color: 'var(--color-text)' }}>
                      <FiUser className="mr-2" style={{ color: 'var(--color-accent)' }} />
                      Profile
                    </h1>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setShowModal(true)}
                        aria-label="Sign out"
                        className="p-2 rounded-lg border-2 transition-all duration-200 flex items-center gap-2 text-sm font-medium hover:shadow-md hover:scale-105"
                        style={{
                          borderColor: 'var(--color-secondary)',
                          color: '#dc2626',
                          backgroundColor: 'transparent'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#dc262610';
                          e.currentTarget.style.borderColor = '#dc2626';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderColor = 'var(--color-secondary)';
                        }}
                      >
                        <FiLogOut className="h-4 w-4" />
                        <span className="hidden sm:inline">Sign Out</span>
                      </button>
                      <AnimatedThemeToggle isDarkMode={isDarkMode} toggleTheme={toggleTheme} />
                    </div>
                  </motion.div>

                  {/* Avatar */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.2 }}
                    className="flex justify-center mb-6 relative"
                  >
                    <div className="relative group">
                      <img
                        src={data.photoURL}
                        alt="avatar"
                        className="h-28 w-28 rounded-xl object-cover border-4 shadow-lg transition-transform duration-200 hover:scale-105"
                        style={{ borderColor: 'var(--color-accent)' }}
                      />
                      <button
                        onClick={() => inputFile.current.click()}
                        aria-label="Upload new avatar"
                        className="absolute bottom-0 right-0 p-2 rounded-full transition-all duration-300 shadow-md hover:shadow-lg border-2 hover:scale-110"
                        style={{ 
                          backgroundColor: 'var(--color-accent)',
                          borderColor: 'var(--color-primary)',
                          color: 'var(--color-primary)',
                          transform: 'translate(20%, 20%)'
                        }}
                      >
                        {photoBusy ? (
                          <div className="h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <FiCamera className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      ref={inputFile}
                      onChange={handlePhoto}
                      className="hidden"
                      aria-label="Select new profile picture"
                    />
                  </motion.div>

                  {/* Info rows */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.3 }}
                    className="space-y-4 p-4 rounded-lg border mb-6 flex-grow"
                    style={{
                      backgroundColor: 'var(--color-secondary)/20',
                      borderColor: 'var(--color-secondary)'
                    }}
                  >
                    {[
                      { icon: FiUser, label: "Name", name: "name", value: data.name },
                      { icon: FiHash, label: "ID number", name: "idNumber", value: data.idNumber },
                      { icon: FiMail, label: "Email", name: "email", value: data.email },
                      { icon: FiShield, label: "Role", name: "role", value: data.role },
                    ].map((item, index) => (
                      <div key={item.name}>
                        <InfoRow 
                          icon={item.icon} 
                          label={item.label} 
                          name={item.name} 
                          editable={false} 
                          value={item.value} 
                        />
                      </div>
                    ))}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleSignOut}
        title="Sign Out"
        message="Are you sure you want to sign out?"
      />
    </div>
  );
}
